import * as _ from 'lodash';
import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { Denomination } from '~models/denomination.model';
import { ETagProduct, ETagProductNameMapping, ETagType, GiftCardV2 } from '~models/gift-card.model';
import { environment } from '../../../../environments/environment';
import { constants } from '../../../app.constants';
import { isRenderingForMobileApp } from 'src/constants';
import { Currency } from '~models/currency.model';
import { Utils } from 'src/app/utils/utilities';
import { CheckoutStateService } from 'src/app/shared-data/checkout-state.service';
import { TrxService, DEFAULT_CROSS_DOMAIN_CSS_CLASS, MarkdownService } from '~services';
import { CheckoutEvent, Vendor } from '~models';
import { Subject } from 'rxjs';
import { ZipYiElement } from '~services/biocatch/zip-yi-element.enum';
import { Router } from '@angular/router';
import { SelectOptionV2Service } from 'src/app/shared-data';

@Component({
  selector: 'app-new-design-variant-v2',
  templateUrl: './new-design-variant.component.html',
  styleUrls: ['./new-design-variant.component.scss'],
})
export class NewDesignVariantV2Component implements OnInit, OnDestroy, OnChanges {
  titleSelectOption: string;
  buyTypeConst: any;
  giftingEnabled: boolean;
  numTruncate: number;
  isLongDesc: boolean;
  isSeeMore: boolean;

  @Input()
  public customDenomination: Denomination = {
    isCustom: true,
    currency: Currency.AUD,
    price: 0,
    stock: 'IN_STOCK',
  };

  @Input()
  public isMobileRender = isRenderingForMobileApp;

  @Output()
  public readonly checkout = new EventEmitter<CheckoutEvent>();

  @Output()
  public readonly back = new EventEmitter<void>();

  @Input()
  public readonly giftCard: GiftCardV2;

  @Input()
  public readonly animate = true;

  public readonly appPrefix = environment.appPrefix;

  public showDisclaimer = false;

  public showDelivery = false;

  @Input()
  public browseCards: GiftCardV2[] = [];

  @Output()
  public readonly continueShopping = new EventEmitter<void>();

  @Input()
  public cardAvailability: boolean;

  @Input()
  public buyType;

  @Output()
  public readonly buyTypeChange = new EventEmitter<string>();

  public showProductFeatures = false;

  cashbackPercentage = '';
  buyForFriendLabel = 'Buy for a friend';
  buyForMyselfLabel = 'Buy for myself';
  crossDomainCssClass = DEFAULT_CROSS_DOMAIN_CSS_CLASS;
  zipYiElement = ZipYiElement;

  public originalDescription: string;

  private SEOScriptId: string;
  private destroy$ = new Subject<boolean>();

  constructor(
    public selectOptionV2Service: SelectOptionV2Service,
    public checkoutStateService: CheckoutStateService,
    private trxService: TrxService,
    private markdownService: MarkdownService,
    private router: Router,
  ) {
    this.titleSelectOption = 'Value';
    this.buyTypeConst = constants.BUY_TYPES;
    this.giftingEnabled = true;
    this.numTruncate = 224;
    this.isLongDesc = false;
    this.isSeeMore = false;
  }

  async ngOnInit() {
    this.resetState();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes?.giftCard?.currentValue?.id !== changes?.giftCard?.previousValue?.id) {
      this.resetState();
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next(true);
    Utils.removeScriptInHeadById(this.SEOScriptId);
  }

  private async resetState() {
    this.initDescription();
    this.checkoutStateService.setStateCheckoutBtnTriggered(false);
    this.updateSEOScript();
  }

  checkAppVersion(): boolean {
    const agent = navigator.userAgent.toLowerCase(),
      start = agent.indexOf('/'),
      end = agent.indexOf(' ', start),
      appInfo = agent.substr(0, end).split('/');
    if (!appInfo[0].includes('zip')) {
      return false;
    }

    const compareVersion = (requiredVersion: number[]) => {
      const currentVersion = appInfo[1].split('.').map(Number);
      for (let i = 0; ; i++) {
        if (i >= requiredVersion.length) {
          return true;
        }
        if (i >= currentVersion.length) {
          return false;
        }
        const diff = currentVersion[i] - requiredVersion[i];
        if (diff) {
          return diff > 0 ? true : false;
        }
      }
    };

    if (/android/.test(agent) || (/iphone|ipod|ipad/.test(agent) && start > -1)) {
      return compareVersion([1, 2, 8]);
    } else {
      return false;
    }
  }

  async doCheckout() {
    await this.trxService.sendUIClickEvent({
      control_type: 'button',
      label: this.buyForMyselfLabel,
      action_type: 'click',
      control_id: null,
      destination_target_url: null,
    });

    if (!Utils.checkValid(this.selectOptionV2Service.selectedDenomination)) {
      this.selectOptionV2Service.isValidData = false;

      return;
    }

    return this.checkout.emit();
  }

  doCheckoutForFriend() {
    return this.checkout.emit({ isGifting: true });
  }

  async chooseBuyType(type: string) {
    await this.trxService.sendUIClickEvent({
      control_type: 'button',
      label: type === this.buyTypeConst.FRIEND ? this.buyForFriendLabel : this.buyForMyselfLabel,
      action_type: 'click',
      control_id: null,
      destination_target_url: null,
    });

    this.buyType = type;
    this.buyTypeChange.emit(this.buyType);
  }

  doTruncate(text: string) {
    const dataTemp = text;
    if (dataTemp.length > this.numTruncate) {
      return dataTemp.slice(0, this.numTruncate) + ' ... ';
    }
    return dataTemp;
  }

  public get plainPromoTag(): string {
    const plainPromo = this.giftCard.tags?.find((t) => t.type === ETagType.PLAIN);
    if (!plainPromo) return '';

    return plainPromo.text;
  }

  public get cashbackPromoTag(): string {
    const cashbackPromo = this.giftCard.tags?.find((t) => t.type === ETagType.CASHBACK);
    if (!cashbackPromo) {
      return '';
    }

    const promoProductInfo =
      cashbackPromo.products?.length === 1
        ? ` with ${ETagProductNameMapping[cashbackPromo.products[0]]}`
        : '';

    // If cashback percentage is hidden, show a static text
    const isLockedState = cashbackPromo.text === 'Cashback deal';

    if (isLockedState) {
      return `Cashback ${promoProductInfo}`;
    }

    return `${cashbackPromo.text}${promoProductInfo}`;
  }

  public get terms() {
    return this.markdownService.renderInline(`${this.giftCard.terms ?? ''}`);
  }

  public get description() {
    if (this.isSeeMore || !this.isLongDesc) {
      return this.markdownService.renderInline(this.giftCard.importantContent ?? '');
    } else {
      return this.getTruncatedDescription();
    }
  }

  public get showCashBackDisclaimer() {
    const cashbackPromo = this.giftCard.tags?.find((t) => t.type === ETagType.CASHBACK);
    if (cashbackPromo) {
      this.cashbackPercentage = cashbackPromo.text?.match(/(\d*(\.\d+)?)%/g)?.[0];
    }

    return (
      cashbackPromo?.products?.length === 1 &&
      cashbackPromo?.products[0] === ETagProduct.ZM &&
      cashbackPromo.text !== 'Cashback deal'
    );
  }

  downloadApp() {
    window.location.href = 'https://zip.co/au/app';
  }

  showMore() {
    this.isSeeMore = true;
  }

  showLess() {
    this.isSeeMore = false;
  }

  public initDescription() {
    if (
      this.giftCard.importantContent &&
      this.giftCard.importantContent.length > this.numTruncate
    ) {
      this.isLongDesc = true;
      this.isSeeMore = false;
    } else {
      this.isLongDesc = false;
      this.isSeeMore = false;
    }
  }

  public getTruncatedDescription() {
    if (this.giftCard.importantContent) {
      return this.doTruncate(
        this.markdownService.renderInline(this.giftCard.importantContent ?? ''),
      );
    }
  }

  public get isGifEnabled() {
    return this.giftCard.vendor !== Vendor.Eonx;
  }

  public get displayName() {
    return `${
      this.giftCard.vendor === Vendor.DigitalGlue
        ? this.giftCard.brand.replace(/Store\s?card/i, '')
        : this.giftCard.brand
    } Gift Card`;
  }

  private updateSEOScript() {
    if (this.SEOScriptId && Utils.getScriptInHead(this.SEOScriptId)) {
      Utils.removeScriptInHeadById(this.SEOScriptId);
    }

    this.SEOScriptId = `${this.giftCard.id}_SEO_Script`;
    const structuredProductDataForSEO = JSON.stringify(this.constructStructuredProductData());
    Utils.setScriptInHead(this.SEOScriptId, 'application/ld+json', structuredProductDataForSEO);
  }

  private constructStructuredProductData() {
    const sortedDenominations = _.sortBy(this.giftCard.denominations, [(o) => o.price]);
    return {
      '@context': 'https://schema.org/',
      '@type': 'Product',
      name: this.displayName,
      image: [this.giftCard.image],
      description: `Buy ${this.giftCard.brand} gift cards online with Zip and receive them instantly via email. Simply load up your ${this.giftCard.brand} gift vouchers to spend online or in-store.`,
      sku: this.giftCard.brand,
      brand: { '@type': 'Brand', name: this.giftCard.brand },
      offers: {
        '@type': 'AggregateOffer',
        lowPrice: sortedDenominations[0].price,
        highPrice: sortedDenominations[sortedDenominations.length - 1].price,
        priceCurrency: sortedDenominations[0].currency,
        availability: 'https://schema.org/InStock',
        itemCondition: 'https://schema.org/NewCondition',
        shippingDetails: {
          '@type': 'OfferShippingDetails',
          shippingRate: {
            '@type': 'MonetaryAmount',
            value: '0',
            currency: sortedDenominations[0].currency,
          },
        },
      },
    };
  }

  public browseClick(id: string) {
    this.router.navigate(['/checkout', id]);
  }
}
